// Per-coin trade store backed by `useSyncExternalStore`.
//
// Two reasons we hand-rolled instead of pulling Zustand:
//  1. Cockpit is one feature in a larger app — avoiding a new global state
//     dep keeps bundle smaller and reasoning simpler.
//  2. The shape is intentionally narrow: just three slices per coin
//     (whaleTrades, liquidations, summary) plus a per-coin version int
//     that lets React's `useSyncExternalStore` cheap-equality check.
//
// Selector hooks (see useCoinTrades.js, useCoinSummary.js) subscribe to a
// single coin's slice — when BTC updates, only widgets reading BTC re-render.
// ETH-bound widgets are untouched.

// coin -> { whaleTrades: [], liquidations: [], summary: null, version: 0 }
const slices = new Map();

// coin -> Set<listener>. Per-coin subscribers — coarsest practical fanout.
// Selector hooks fire their state-equality check downstream, so this
// granularity is fine.
const listeners = new Map();

function emptySlice() {
  return { whaleTrades: [], liquidations: [], summary: null, version: 0 };
}

function notify(coin) {
  const set = listeners.get(coin);
  if (!set) return;
  // Copy to array before iterating so a listener unsubscribing during a
  // notify doesn't mutate the live set mid-loop.
  for (const fn of [...set]) {
    try { fn(); } catch { /* ignore listener errors */ }
  }
}

export const tradeStore = {
  ensureSlice(coin) {
    if (!slices.has(coin)) slices.set(coin, emptySlice());
  },

  getSlice(coin) {
    return slices.get(coin) || emptySlice();
  },

  subscribe(coin, listener) {
    let set = listeners.get(coin);
    if (!set) { set = new Set(); listeners.set(coin, set); }
    set.add(listener);
    return () => {
      set.delete(listener);
      if (set.size === 0) listeners.delete(coin);
    };
  },

  // Wholesale replace this coin's history (initial coin_data dump).
  setCoinHistory(coin, { whaleTrades, liquidations, summary }, maxPerList) {
    const cur = slices.get(coin) || emptySlice();
    const cappedW = whaleTrades.length > maxPerList ? whaleTrades.slice(0, maxPerList) : whaleTrades;
    const cappedL = liquidations.length > maxPerList ? liquidations.slice(0, maxPerList) : liquidations;
    slices.set(coin, {
      whaleTrades: cappedW,
      liquidations: cappedL,
      summary: summary || cur.summary,
      version: cur.version + 1,
    });
    notify(coin);
  },

  // Prepend incoming live trades + trim. WS sends newest-last; we prepend
  // so the chronological order in the array matches the existing client
  // expectations (newest at index 0).
  appendTrades(coin, { whale, liq }, maxPerList) {
    const cur = slices.get(coin) || emptySlice();
    const nextWhale = whale.length > 0
      ? [...whale.slice().reverse(), ...cur.whaleTrades].slice(0, maxPerList)
      : cur.whaleTrades;
    const nextLiq = liq.length > 0
      ? [...liq.slice().reverse(), ...cur.liquidations].slice(0, maxPerList)
      : cur.liquidations;

    // Mutate summary in-place if we got new entries — counters tick up.
    let nextSummary = cur.summary;
    if (cur.summary && (whale.length > 0 || liq.length > 0)) {
      let totalWhaleTrades = cur.summary.totalWhaleTrades || 0;
      let totalLiquidations = cur.summary.totalLiquidations || 0;
      let whaleVolume = cur.summary.whaleVolume || 0;
      let liqVolume = cur.summary.liqVolume || 0;
      let totalVolume = cur.summary.totalVolume || 0;
      for (const t of whale) {
        const s = t?.dollarSize || 0;
        totalWhaleTrades += 1; whaleVolume += s; totalVolume += s;
      }
      for (const l of liq) {
        const s = l?.dollarSize || 0;
        totalLiquidations += 1; liqVolume += s; totalVolume += s;
      }
      nextSummary = { ...cur.summary, totalWhaleTrades, totalLiquidations, whaleVolume, liqVolume, totalVolume };
    }

    slices.set(coin, {
      whaleTrades: nextWhale,
      liquidations: nextLiq,
      summary: nextSummary,
      version: cur.version + 1,
    });
    notify(coin);
  },

  // Server-pushed summary refresh (more authoritative than our local tick-up).
  setSummary(coin, summary) {
    const cur = slices.get(coin) || emptySlice();
    slices.set(coin, { ...cur, summary, version: cur.version + 1 });
    notify(coin);
  },
};
